// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { ApolloError } from '@apollo/client';

import { useRooftop, useUser } from '@store';
import { useValetSelfManaged } from '@hopdrive/feature-portal-products';
import { Moves } from '@gql/base-types';
import { useGetMoveSearchResultsLazyQuery } from '../queries/getMoveSearchResults.query.generated';
import { generateMovesCSV } from '@services/csvService';

import { css, keyframes } from '@emotion/css';
import { useTheme, Theme, LinearProgress } from '@mui/material';
import { BiFile } from 'react-icons/bi';

import AssignOrUnassignMoveModal from '@components/Modals/AssignOrUnassignMoveModal';

import DefaultFallback from '@components/Fallbacks/DefaultFallback';
import Spacer from '@components/Utils/Spacer';
import Toolbar from '@components/Utils/Toolbar';

import { useSearchResults } from '@features/searchResults/providers/SearchResultsProvider';

import SearchResultsControls from '@features/searchResults/components/SearchResultsControls';
import SearchResultsGrid from '@features/searchResults/components/SearchResultsGrid';
import SearchResultsPagination from '@features/searchResults/components/SearchResultsPagination';
import SearchResultsTable from '@features/searchResults/components/SearchResultsTable';
import SearchResultsToolbar from '@features/searchResults/components/SearchResultsToolbar';
import ExportResultsDialog from '@components/Modals/ExportResultsDialog';

// TYPES ---------------------------------------------------------------- //

interface SearchResultsContentProps {
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => Promise<void>;
  moves: Moves[];
  moveCount: number;
  searchInput: string;
}

// COMPONENT ---------------------------------------------------------------- //

const SearchResultsContent = ({
  loading,
  error,
  refetch,
  moves,
  moveCount,
  searchInput,
}: SearchResultsContentProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const user = useUser();
  const rooftop = useRooftop();

  const isAdmin =
    user?.profile?.default_role === 'admin' ||
    user?.profile?.default_role === 'dealer-super-admin' ||
    user?.profile?.default_role === 'dealer-admin' ||
    user?.profile?.default_role === 'program-admin';
  const isConcierge = rooftop?.category !== 'operational';
  // Self-managed-capable (self-managed or mixed) — the search grid's self-managed rendering.
  const isLite = useValetSelfManaged();

  const {
    disablePickers,
    end,
    limit,
    offset,
    onClearFilters,
    onDateChange,
    onRangeChange,
    onViewModeChange,
    orderBy,
    range,
    setLimit,
    setOffset,
    setOrder,
    setOrderBy,
    start,
    viewMode,
  } = useSearchResults();

  const [loadingVisible, setLoadingVisible] = React.useState<boolean>(false);
  React.useEffect(() => {
    if (loading) {
      setLoadingVisible(true);
    } else {
      setTimeout(() => setLoadingVisible(false), 250);
    }
  }, [loading]);

  const [listOfAssigning, setListOfAssigning] = React.useState<any[]>([]);
  const [exportDialogOpen, setExportDialogOpen] = React.useState(false);

  // Lazy query for fetching all results for export
  const [fetchAllMoves] = useGetMoveSearchResultsLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const [assignOrUnassignModal, setAssignOrUnassignModal] = React.useState<{ open: boolean; input: any; output: any }>({
    open: false,
    input: null,
    output: null,
  });
  const handleAssignOrUnassignModalOpen = (input?: any) => {
    setAssignOrUnassignModal({ ...assignOrUnassignModal, open: true, input });
  };
  const handleAssignOrUnassignModalClose = (output?: any) => {
    setAssignOrUnassignModal({ ...assignOrUnassignModal, open: false, output });
    if (output) setListOfAssigning(prev => [...prev, output]);
  };

  // Export handlers
  const handleExportAll = async () => {
    // Fetch all results without pagination
    const { data } = await fetchAllMoves({
      variables: {
        start,
        end,
        limit: moveCount, // Fetch all results
        offset: 0,
        searchInputNumber: Number(searchInput) || 0,
        searchInputString: `%${searchInput}%`,
      },
    });

    if (data?.moves) {
      generateMovesCSV(data.moves as Moves[], start, end, isAdmin);
    }
  };

  const handleExportCurrentPage = () => {
    generateMovesCSV(moves, start, end, isAdmin);
  };

  // This useEffect is used to remove the loading state from a move after a driver is assigned/reassigned/unassigned to it
  // This is necessary because the modal doesnt wait for the backend to complete before closing
  // We do this by looking for the expected driver id on the move or latest offer after a refetch
  // Once the desired state of the move is reached, we remove the id from the list of assigning
  React.useEffect(() => {
    if (!loading && !error && moves && listOfAssigning?.length) {
      // Map through the list of assigning and check if the move is in the data
      listOfAssigning.forEach(assigning => {
        const foundMove = moves?.find(move => move?.id === assigning?.moveId);
        const foundDriverId = foundMove?.driver_id;
        const foundOfferDriverId =
          foundMove?.scenariotomoves?.[0]?.scenario?.bundletoscenarios?.[0]?.bundle?.offers?.[0]?.driver?.id;

        if (foundMove) {
          if (assigning?.action === 'assign' || assigning?.action === 'reassign') {
            if (foundDriverId === assigning?.driverId || foundOfferDriverId === assigning?.driverId) {
              setListOfAssigning(prev => prev?.filter(assigning => assigning?.moveId !== foundMove?.id));
            }
          }
          if (assigning?.action === 'unassign') {
            if (foundDriverId === assigning?.driverId || foundOfferDriverId === assigning?.driverId) {
              setListOfAssigning(prev => prev?.filter(assigning => assigning?.moveId !== foundMove?.id));
            }
          }
        }
      });
    }
  }, [loading, error, moves]);

  return (
    <>
      <AssignOrUnassignMoveModal
        open={assignOrUnassignModal?.open}
        onClose={handleAssignOrUnassignModalClose}
        moveInput={assignOrUnassignModal?.input?.move}
        offersInput={assignOrUnassignModal?.input?.offers}
      />

      <ExportResultsDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        title='Export Search Results'
        noun='result'
        totalCount={moveCount}
        currentPageCount={moves.length}
        onExportAll={handleExportAll}
        onExportCurrentPage={handleExportCurrentPage}
      />

      <div className={cls.header}>
        <Toolbar
          back
          fullscreen
          zIndex={0}
          position='relative'
          title={`Found ${moveCount || 0} results for "${searchInput?.replace(/%/g, '')}"`}
          refetch={refetch}
          onClear={onClearFilters}
          actions={[
            {
              label: 'Export CSV',
              icon: <BiFile style={{ marginRight: 8, color: theme?.palette?.text?.primary }} />,
              handler: () => setExportDialogOpen(true),
              disabled: moveCount === 0,
            },
          ]}
        >
          <SearchResultsToolbar
            isConcierge={isConcierge}
            isLite={isLite}
            viewMode={viewMode}
            onViewModeChange={onViewModeChange}
            orderBy={orderBy}
            setOrderBy={setOrderBy}
            setOrder={setOrder}
            setOffset={setOffset}
          />
        </Toolbar>

        <Spacer size={3} />

        <div className={cls.headerDetails}>
          <SearchResultsControls
            range={range}
            start={start}
            end={end}
            disablePickers={disablePickers}
            onRangeChange={onRangeChange}
            onDateChange={onDateChange}
          />
        </div>
      </div>

      {loadingVisible ? (
        <LinearProgress className={loading ? cls.fadeIn : cls.fadeOut} />
      ) : (
        <div className={cls.loadingPad} />
      )}

      {error ? (
        <>
          <Spacer size={2} />
          <DefaultFallback fullscreen severity='error' message='Failed to fetch moves.' />
        </>
      ) : null}

      {!loading && !error && !moves.length ? (
        <>
          <Spacer size={2} />
          <DefaultFallback
            fullscreen
            severity='warning'
            message='No moves found. Try adjusting your date range, metric or other filters...'
          />
        </>
      ) : null}

      {!error && moves.length > 0 ? (
        <>
          {/* TOP PAGINATION */}
          <SearchResultsPagination
            moveCount={moveCount}
            offset={offset}
            limit={limit}
            setOffset={setOffset}
            setLimit={setLimit}
          />

          {/* CONTENT */}
          <div className={loading ? cls.contentLoading : cls.content}>
            {viewMode === 'table' ? (
              <SearchResultsTable moves={moves} moveCount={moveCount} isAdmin={isAdmin} payersArray={[]} />
            ) : (
              <SearchResultsGrid
                moves={moves}
                orderBy={orderBy}
                listOfAssigning={listOfAssigning}
                handleAssignOrUnassignModalOpen={handleAssignOrUnassignModalOpen}
              />
            )}

            {/* BOTTOM PAGINATION - Inside of content div to avoid layout shift */}
            <SearchResultsPagination
              moveCount={moveCount}
              offset={offset}
              limit={limit}
              setOffset={setOffset}
              setLimit={setLimit}
            />
          </div>
        </>
      ) : null}
    </>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme: Theme) => {
  const fadeIn = keyframes`
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  `;

  const fadeOut = keyframes`
    0% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  `;

  const contentFadeIn = keyframes`
    0% {
      opacity: 0;
    }
    33% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  `;

  return {
    header: css`
      background-color: ${theme.palette.background.paper};
      border-bottom: 1px solid ${theme.palette.divider};
    `,
    headerDetails: css`
      padding: ${theme.spacing(0, 2, 2, 2)};
    `,

    fadeIn: css`
      opacity: 1;
      animation: ${fadeIn} 0.25s ease-in-out;
    `,
    fadeOut: css`
      opacity: 0;
      animation: ${fadeOut} 0.25s ease-in-out;
    `,
    loadingPad: css`
      width: 100%;
      height: 4px;
    `,

    content: css`
      opacity: 1;
      transition: opacity 0.25s ease-in-out;
      animation: ${contentFadeIn} 0.5s ease-in-out;
    `,
    contentLoading: css`
      opacity: 0;
      transition: opacity 0.25s ease-in-out;
      user-select: none;
      pointer-events: none;
    `,
  };
};

// EXPORT ---------------------------------------------------------------- //

export default SearchResultsContent;
